/**
 * @fileoverview Syndicator Showdown — Leaderboards page default-selection +
 * selector-ordering helpers
 * @module utils/leaderboardSelection
 *
 * Pure, dependency-free helpers used by LeaderboardsPage.jsx to decide which
 * game_slates doc should be selected by default, and how the game selector
 * dropdown should be ordered/labelled. Kept free of Firestore imports so
 * they're trivially unit-testable and reusable.
 *
 * These deliberately rely on the SAME denormalized `completedRaces`/
 * `totalRaces` counters as utils/showdownStatus.js rather than requiring a
 * waterfall of per-candidate races-subcollection reads — one already-fetched
 * slate list is enough to make a correct decision.
 */

import { toMs, getRaceProgress, isFinalisedSlate } from './showdownStatus';

/**
 * True if a resulted-race count can be determined for `slate`, either from
 * an optional races array (freshest) or the denormalized slate counters.
 */
function resolveRaceMeta(slate, racesBySlate) {
  const races = racesBySlate?.[slate.id];
  if (Array.isArray(races) && races.length > 0) {
    const resultedRaces = races.filter(r => r?.status === 'resulted').length;
    return { resultedRaces, hasAnyResult: resultedRaces > 0 };
  }
  const progress = getRaceProgress(slate);
  return { resultedRaces: progress.resultedRaces, hasAnyResult: progress.hasAnyResult };
}

/**
 * Builds the set of slateIds the user has a real (submitted/resulted, never
 * an unsubmitted draft) entry in.
 * @param {Array} userEntries - player_entries docs, each with slateId + status
 */
export function buildEnteredSlateIdSet(userEntries) {
  return new Set(
    (userEntries || [])
      .filter(e => e && (e.status === 'submitted' || e.status === 'resulted'))
      .map(e => e.slateId)
  );
}

function slateRecencyMs(slate) {
  return toMs(slate?.gameCloseTime) || toMs(slate?.meetingDate) || toMs(slate?.gameOpenTime) || 0;
}

function annotateSlates(slates, enteredSlateIds, racesBySlate, leaderboardRowsBySlate) {
  return (slates || []).map(slate => {
    const { resultedRaces, hasAnyResult: hasAnyResultFromRaces } = resolveRaceMeta(slate, racesBySlate);
    const rows = leaderboardRowsBySlate?.[slate.id];
    const hasAnyResult = hasAnyResultFromRaces || (Array.isArray(rows) && rows.length > 0);
    return {
      slate,
      resultedRaces,
      hasAnyResult,
      isFinalised: isFinalisedSlate(slate),
      isEntered: enteredSlateIds.has(slate.id),
      recencyMs: slateRecencyMs(slate),
    };
  });
}

const byRecencyDesc = (a, b) => b.recencyMs - a.recencyMs;

/**
 * Resolves the default leaderboard slate for the Leaderboards menu page.
 * Priority order (first non-empty tier wins):
 *
 *   1. User-entered, NOT finalised, >=1 race resulted (in-play + results)
 *   2. User-entered, finalised (most recent completed game the user entered)
 *   3. User-entered, NOT finalised, 0 races resulted (active/upcoming, no results)
 *   4. NOT entered by user, NOT finalised, >=1 race resulted (best public in-play game)
 *   5. NOT entered by user, finalised (most recent completed public game)
 *   6. Nothing usable at all -> null (caller shows a clean empty state)
 *
 * Every tier is sorted newest-first (gameCloseTime, falling back to
 * meetingDate/gameOpenTime) before taking the top candidate.
 *
 * @param {Object} params
 * @param {Array}  params.userEntries          - player_entries docs (slateId, status)
 * @param {Array}  params.slates               - game_slates docs
 * @param {Object} [params.racesBySlate]       - optional { [slateId]: races[] } for fresher resulted-count than the slate's own counters
 * @param {Object} [params.leaderboardRowsBySlate] - optional { [slateId]: leaderboardRows[] } — a non-empty board is also treated as "has results"
 * @returns {string|null} slateId, or null if there's nothing relevant to show
 */
export function getDefaultLeaderboardSlate({
  userEntries = [],
  slates = [],
  racesBySlate = {},
  leaderboardRowsBySlate = {},
} = {}) {
  if (!slates || slates.length === 0) return null;

  const enteredSlateIds = buildEnteredSlateIdSet(userEntries);
  const annotated = annotateSlates(slates, enteredSlateIds, racesBySlate, leaderboardRowsBySlate);

  const tier1 = annotated.filter(m => m.isEntered && !m.isFinalised && m.hasAnyResult).sort(byRecencyDesc);
  if (tier1.length > 0) return tier1[0].slate.id;

  const tier2 = annotated.filter(m => m.isEntered && m.isFinalised).sort(byRecencyDesc);
  if (tier2.length > 0) return tier2[0].slate.id;

  const tier3 = annotated.filter(m => m.isEntered && !m.isFinalised && !m.hasAnyResult).sort(byRecencyDesc);
  if (tier3.length > 0) return tier3[0].slate.id;

  const tier4 = annotated.filter(m => !m.isEntered && !m.isFinalised && m.hasAnyResult).sort(byRecencyDesc);
  if (tier4.length > 0) return tier4[0].slate.id;

  const tier5 = annotated.filter(m => !m.isEntered && m.isFinalised).sort(byRecencyDesc);
  if (tier5.length > 0) return tier5[0].slate.id;

  return null;
}

/**
 * Status label for the leaderboard selector dropdown badges. Distinct from
 * showdownStatus.js's `computeStatus` (which drives the Showdowns page's
 * finer-grained countdown/timing display) — here LIVE vs RESULTS PENDING is
 * determined by whether a race has actually resulted yet, matching what a
 * player expects to see when picking a game to view results for.
 *
 *   COMPLETED       - finalised by an operator
 *   LIVE            - not finalised, >=1 race resulted
 *   RESULTS PENDING - not finalised, 0 results, but racing has started/closed
 *   UPCOMING        - not finalised, 0 results, hasn't started yet
 */
export function getLeaderboardStatusLabel(slate) {
  if (isFinalisedSlate(slate)) return 'COMPLETED';

  const { hasAnyResult } = getRaceProgress(slate);
  if (hasAnyResult) return 'LIVE';

  const now = Date.now();
  const openMs = toMs(slate?.gameOpenTime);
  const closeMs = toMs(slate?.gameCloseTime);
  const hasStarted = (closeMs && now >= closeMs) || (openMs && now >= openMs);

  return hasStarted ? 'RESULTS PENDING' : 'UPCOMING';
}

const SELECTOR_TIER = {
  ENTERED_LIVE: 0,
  ENTERED_COMPLETED: 1,
  ENTERED_UPCOMING: 2,
  PUBLIC_LIVE: 3,
  PUBLIC_COMPLETED: 4,
  OTHER: 5,
};

function selectorTierOf(meta) {
  if (meta.isEntered && !meta.isFinalised && meta.hasAnyResult) return SELECTOR_TIER.ENTERED_LIVE;
  if (meta.isEntered && meta.isFinalised) return SELECTOR_TIER.ENTERED_COMPLETED;
  if (meta.isEntered && !meta.isFinalised && !meta.hasAnyResult) return SELECTOR_TIER.ENTERED_UPCOMING;
  if (!meta.isEntered && !meta.isFinalised && meta.hasAnyResult) return SELECTOR_TIER.PUBLIC_LIVE;
  if (!meta.isEntered && meta.isFinalised) return SELECTOR_TIER.PUBLIC_COMPLETED;
  return SELECTOR_TIER.OTHER;
}

/**
 * Orders game_slates for the Leaderboards selector dropdown by relevance:
 *   1. In-play user-entered games with results
 *   2. Completed user-entered games
 *   3. Active user-entered games with no results
 *   4. Public in-play games with results
 *   5. Public completed games
 *   6. Everything else (e.g. public upcoming games with no results)
 * Newest-first within each tier.
 *
 * @param {Array} slates - game_slates docs
 * @param {Set|Array} enteredSlateIds - slateIds the user has a real entry in
 */
export function sortLeaderboardSelectorOptions(slates, enteredSlateIds) {
  const entered = enteredSlateIds instanceof Set ? enteredSlateIds : new Set(enteredSlateIds || []);
  const annotated = annotateSlates(slates, entered, {}, {});
  return [...annotated]
    .sort((a, b) => {
      const ta = selectorTierOf(a);
      const tb = selectorTierOf(b);
      if (ta !== tb) return ta - tb;
      return b.recencyMs - a.recencyMs;
    })
    .map(m => m.slate);
}
